import { FC, MouseEvent } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { FieldError, useForm } from "react-hook-form";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import Button from "@/components/Button";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import { Dropdown, Option } from "@/components/Forms/Dropdown";
import { api } from "@/utils/api";
import { handleFormErrors, ucFirst } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import useAlerts from "@/hooks/useAlerts";
import { ServerResponse, TestingChannelResource } from "@/types/resources";

const networks = [
  {
    value: "facebook",
    label: "Facebook",
  },
  {
    value: "youtube",
    label: "Youtube",
  },
];

type FormDataT = {
  link: string;
  network: Option | null;
  notes: string;
};

interface Props {
  onClose: () => void;
  channel?: TestingChannelResource;
}

const TestingChannelEditModal: FC<Props> = ({ onClose, channel }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      link: channel?.url || "",
      network: channel?.network
        ? { value: channel.network, label: ucFirst(channel.network) }
        : networks[1],
      notes: channel?.notes || "",
    },
  });

  const onSubmit = handleSubmit(async ({ link, notes, ...formData }) => {
    try {
      const data = {
        link,
        notes,
        network: formData.network?.value || null,
      };

      if (channel) {
        // Update existing resource
        const updatedChannel = await api.put<
          { notes: string },
          TestingChannelResource
        >(`/v1/testing-channels/${channel.id}`, {
          notes,
        });
        queryClient.setQueryData(
          ["testing_channels"],
          (channels: TestingChannelResource[] | undefined) =>
            channels!.map((channel) => {
              if (channel.id === updatedChannel.id) return updatedChannel;
              return channel;
            })
        );
      } else {
        // Create new resource
        const { error, data: newTestingChannel } = await api.post<
          typeof data,
          ServerResponse<TestingChannelResource>
        >(`/v1/testing-channels/`, data);
        if (error) {
          return alerts.error(error);
        }
        queryClient.setQueryData(
          ["testing_channels"],
          (channels: TestingChannelResource[] | undefined) => [
            newTestingChannel!,
            ...(channels || []),
          ]
        );
      }

      onClose();
      alerts.success(
        `Testing channel was successfully 
          ${!!channel ? "updated" : "created"}`
      );
    } catch (error) {
      handleFormErrors<FormDataT>(error, setError);
    }
  });

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal>
      <SidePanel
        heading={`${!!channel ? "Edit" : "Add"} testing channel`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label htmlFor="linkInput">Channel link</Label>
                <Input
                  $hasError={!!errors.link}
                  id="linkInput"
                  type="url"
                  placeholder="Add channel link"
                  disabled={!!channel}
                  {...register("link")}
                />
                {!!errors.link && (
                  <ValidationMessage>{errors.link.message}</ValidationMessage>
                )}
              </Larger>
              <Larger onClick={(e) => e.stopPropagation()}>
                <Label htmlFor="networkSelect">Network</Label>
                <Dropdown
                  $hasError={!!errors.network}
                  options={networks}
                  control={control}
                  disabled
                  name="network"
                  id="networkSelect"
                />
                {!!errors.network && (
                  <ValidationMessage>
                    {(errors.network as FieldError).message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="notesTextarea">Notes</Label>
                <Textarea
                  id="notesTextarea"
                  placeholder="Add notes"
                  $hasError={!!errors.notes}
                  {...register("notes")}
                />
                {!!errors.notes && (
                  <ValidationMessage>{errors.notes.message}</ValidationMessage>
                )}
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {!!channel ? "Update" : "Add"}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TestingChannelEditModal;
